/**
 * The action row at the bottom of an expanded request card.
 *
 * Extracted from `InboundCard` for its 500-line ceiling when "Send later" was added. It is the row
 * a recruiter is on once they have decided what to do with the draft, so everything that acts on the
 * reply lives here: send, schedule, discard, reminder, clear, rate.
 *
 * ⚠️ **The reply text is read from the LIVE editor, not from `item.draft`**, and the same way for
 * both sending and scheduling. `readEditor` is supplied by the card because the contentEditable ref
 * lives there; it strips the AGG feedback marker, so neither path can ship the internal
 * `data-agg-feedback` span. A scheduled reply is therefore byte-for-byte what sending now would
 * have delivered.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import { ClearActions } from './ClearActions';
import { CreateReminderButton } from './CreateReminderButton';
import { DraftFeedback } from './DraftFeedback';
import { ScheduleReplyButton } from './ScheduleReplyButton';
import { formatSendAt } from './scheduleReplyTime';
import type { InboundRequest } from '../../types/taInboundRequests';

export function InboundCardActions({
  item,
  busy,
  touched,
  onToggle,
  onSendReply,
  onMarkRead,
  onRemoveFromOverview,
  readEditor,
  gmailToken,
  scheduleDisabledReason,
}: {
  item: InboundRequest;
  busy: boolean;
  touched: boolean;
  onToggle: () => void;
  onSendReply: (bodyHtml: string) => void;
  onMarkRead: () => void;
  onRemoveFromOverview: () => void;
  /** The editor's current HTML, feedback markers stripped. Called at click time, never earlier. */
  readEditor: () => string;
  gmailToken: string | null;
  scheduleDisabledReason: string | null;
}) {
  return (
    <div className="flex flex-wrap gap-2 border-t border-gray-100 px-4 py-3" data-no-toggle>
      {/*
        A queued reply REPLACES the send button rather than sitting beside it.
        The card deliberately stays in the list while a reply is scheduled (so nothing vanishes
        unnoticed), which would otherwise leave "Send reply & archive" one click away from sending
        the same answer twice — and the recruiter would only find out from the strip. The server
        refuses the second copy, but a button that looks available and is not is worse than one that
        explains itself.
      */}
      {item.draft && item.scheduledSendAt && (
        <Button size="sm" disabled title="Cancel it in the Scheduled replies strip at the top">
          Scheduled for {formatSendAt(item.scheduledSendAt)}
        </Button>
      )}
      {item.draft && !item.scheduledSendAt && (
        <>
          <Button size="sm" loading={busy} onClick={() => onSendReply(readEditor())}>
            Send reply &amp; archive
          </Button>
          {/*
            Scheduling needs the proxy to hold a refresh token for this recruiter. The card cannot
            know that from here, so the route's 409 is what teaches it — until then the button is
            offered and explains itself if it turns out it cannot work.
          */}
          <ScheduleReplyButton
            threadId={item.threadId}
            opportunityId={item.opportunityId}
            candidateName={item.candidateName}
            getBody={readEditor}
            gmailToken={gmailToken}
            disabledReason={scheduleDisabledReason}
          />
        </>
      )}
      <Button size="sm" variant="outline" onClick={onToggle} disabled={busy}>
        {touched ? 'Discard changes' : 'Close'}
      </Button>
      <CreateReminderButton
        threadId={item.threadId}
        candidateName={item.candidateName}
        subject={item.subject}
      />
      <ClearActions busy={busy} onMarkRead={onMarkRead} onRemoveFromOverview={onRemoveFromOverview} />
      {/*
        Beside the actions rather than above the editor: the rating is about the draft as a whole,
        and this is the row the recruiter is on once they have decided what to do with it. Only when
        there IS a draft — there is nothing to rate otherwise.
      */}
      {item.draft && <DraftFeedback item={item} />}
    </div>
  );
}
